export type TChatMessageRole = "user" | "assistant";
export type TChatMessageType = "text" | "table";

export type TChatTableColumn = {
	key: string;
	header: string;
	widthClass?: string;
	headerClassName?: string;
	cellClassName?: string;
};

export type TChatTableContent = {
	columns: readonly TChatTableColumn[];
	rows: readonly Record<string, string>[];
	rowKey: string;
};

export type TChatPlaybackMessage = {
	id: string;
	role: TChatMessageRole;
	type: TChatMessageType;
	content: string | TChatTableContent;
	delayBeforeMs?: number;
	typingDurationMs?: number;
	/** Reveal text character-by-character with a caret instead of appearing whole. */
	stream?: boolean;
	/** Render without the role avatar. */
	hideAvatar?: boolean;
};

export const CHAT_PLAYBACK_DEFAULTS = {
	delayBeforeMs: 0,
	typingDurationMs: 0,
} as const;

/** Character interval shared with StreamingText so the engine outlasts the visual stream. */
export const CHAT_STREAM_INTERVAL_MS = 8;
export const CHAT_STREAM_SETTLE_MS = 150;
/** Margin so the scroll scene's reveal-all clock always fires after the engine's last timer. */
export const CHAT_PLAYBACK_CLOCK_PAD_MS = 400;

export type EChatPlaybackPhase = "idle" | "waiting" | "typing" | "streaming" | "complete";

export type TChatPlaybackState = {
	phase: EChatPlaybackPhase;
	visibleMessageIds: string[];
	pendingIndex: number;
	typingTargetId: string | null;
};

export type TChatPlaybackEvent =
	| { type: "ACTIVATE" }
	| { type: "REVEAL_ALL" }
	| { type: "RESET" }
	| { type: "PRE_MESSAGE_DELAY_ELAPSED" }
	| { type: "TYPING_ELAPSED" }
	| { type: "STREAM_ELAPSED" };

export type TChatPlaybackEffectIntent = "pre-message" | "typing" | "streaming";

export type TChatPlaybackEffect =
	| { type: "schedule"; delayMs: number; intent: TChatPlaybackEffectIntent }
	| { type: "none" };

export type TChatPlaybackResult = {
	state: TChatPlaybackState;
	effect: TChatPlaybackEffect;
};

export const initialChatPlaybackState: TChatPlaybackState = {
	phase: "idle",
	visibleMessageIds: [],
	pendingIndex: 0,
	typingTargetId: null,
};

function resolveDelayBeforeMs(message: TChatPlaybackMessage | undefined): number {
	return message?.delayBeforeMs ?? CHAT_PLAYBACK_DEFAULTS.delayBeforeMs;
}

function resolveTypingDurationMs(message: TChatPlaybackMessage | undefined): number {
	return message?.typingDurationMs ?? CHAT_PLAYBACK_DEFAULTS.typingDurationMs;
}

function isStreamingAssistantText(message: TChatPlaybackMessage): boolean {
	return message.role === "assistant" && message.stream === true && typeof message.content === "string";
}

function schedulePreMessageDelay(
	state: TChatPlaybackState,
	messages: readonly TChatPlaybackMessage[],
): TChatPlaybackResult {
	const nextMessage = messages[state.pendingIndex];
	if (!nextMessage) {
		return {
			state: { ...state, phase: "complete", typingTargetId: null },
			effect: { type: "none" },
		};
	}

	return {
		state: { ...state, phase: "waiting", typingTargetId: null },
		effect: {
			type: "schedule",
			delayMs: resolveDelayBeforeMs(nextMessage),
			intent: "pre-message",
		},
	};
}

function revealPendingMessage(
	state: TChatPlaybackState,
	messages: readonly TChatPlaybackMessage[],
): TChatPlaybackResult {
	const message = messages[state.pendingIndex];
	if (!message) {
		return {
			state: { ...state, phase: "complete", typingTargetId: null },
			effect: { type: "none" },
		};
	}

	const nextState: TChatPlaybackState = {
		...state,
		visibleMessageIds: [...state.visibleMessageIds, message.id],
		pendingIndex: state.pendingIndex + 1,
		typingTargetId: null,
	};

	if (isStreamingAssistantText(message)) {
		return {
			state: { ...nextState, phase: "streaming" },
			effect: {
				type: "schedule",
				delayMs: String(message.content).length * CHAT_STREAM_INTERVAL_MS + CHAT_STREAM_SETTLE_MS,
				intent: "streaming",
			},
		};
	}

	return schedulePreMessageDelay(nextState, messages);
}

export function reduceChatPlayback(
	state: TChatPlaybackState,
	event: TChatPlaybackEvent,
	messages: readonly TChatPlaybackMessage[],
): TChatPlaybackResult {
	switch (event.type) {
		case "ACTIVATE": {
			if (state.phase !== "idle") {
				return { state, effect: { type: "none" } };
			}
			if (messages.length === 0) {
				return {
					state: { ...state, phase: "complete" },
					effect: { type: "none" },
				};
			}
			return schedulePreMessageDelay(
				{
					...state,
					phase: "waiting",
					visibleMessageIds: [],
					pendingIndex: 0,
					typingTargetId: null,
				},
				messages,
			);
		}

		case "REVEAL_ALL": {
			return {
				state: {
					phase: "complete",
					visibleMessageIds: messages.map((message) => message.id),
					pendingIndex: messages.length,
					typingTargetId: null,
				},
				effect: { type: "none" },
			};
		}

		case "RESET": {
			return { state: initialChatPlaybackState, effect: { type: "none" } };
		}

		case "PRE_MESSAGE_DELAY_ELAPSED": {
			if (state.phase !== "waiting") {
				return { state, effect: { type: "none" } };
			}

			const message = messages[state.pendingIndex];
			if (!message) {
				return {
					state: { ...state, phase: "complete", typingTargetId: null },
					effect: { type: "none" },
				};
			}

			const typingDurationMs = message.role === "assistant" ? resolveTypingDurationMs(message) : 0;
			if (typingDurationMs > 0) {
				return {
					state: {
						...state,
						phase: "typing",
						typingTargetId: message.id,
					},
					effect: {
						type: "schedule",
						delayMs: typingDurationMs,
						intent: "typing",
					},
				};
			}

			return revealPendingMessage(state, messages);
		}

		case "TYPING_ELAPSED": {
			if (state.phase !== "typing") {
				return { state, effect: { type: "none" } };
			}

			return revealPendingMessage(state, messages);
		}

		case "STREAM_ELAPSED": {
			if (state.phase !== "streaming") {
				return { state, effect: { type: "none" } };
			}

			return schedulePreMessageDelay(state, messages);
		}

		default:
			return { state, effect: { type: "none" } };
	}
}

export function selectVisibleMessages(
	messages: readonly TChatPlaybackMessage[],
	visibleMessageIds: readonly string[],
): TChatPlaybackMessage[] {
	const visibleIds = new Set(visibleMessageIds);
	return messages.filter((message) => visibleIds.has(message.id));
}

export function selectIsTyping(state: TChatPlaybackState): boolean {
	return state.phase === "typing";
}

/** The message currently being typed — always the next pending one, so callers can append it to the visible list. */
export function selectTypingMessage(
	messages: readonly TChatPlaybackMessage[],
	state: TChatPlaybackState,
): TChatPlaybackMessage | undefined {
	if (!selectIsTyping(state) || state.typingTargetId === null) return undefined;
	return messages.find((message) => message.id === state.typingTargetId);
}

/**
 * Total wall-clock duration of a transcript, mirroring the reducer's timeline:
 * per message, its pre-message delay, then its typing window, then its stream.
 */
export function computeChatPlaybackDurationMs(messages: readonly TChatPlaybackMessage[]): number {
	return messages.reduce((total, message) => {
		let duration = total + resolveDelayBeforeMs(message);
		if (message.role === "assistant") {
			duration += resolveTypingDurationMs(message);
		}
		if (isStreamingAssistantText(message)) {
			duration += String(message.content).length * CHAT_STREAM_INTERVAL_MS + CHAT_STREAM_SETTLE_MS;
		}
		return duration;
	}, 0);
}
